<template>
  <v-dialog v-model="open" max-width="600px" persistent>
    <v-card class="d-flex flex-column" style="max-height: 90vh;">
      <v-toolbar flat color="secondary">
        <v-toolbar-title class="text-white">
          {{ $t("labels.json-property") }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn color="white" icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <div class="pa-4">
        <v-form v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="10">
              <v-text-field
                v-model="jsonPath"
                readonly
                :label="$t('labels.json-property')"
                variant="outlined"
                required
                density="compact"
                name="jsonPath"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="2" class="d-flex flex-column align-center justify-center pl-2">
              <v-tooltip location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    icon
                    size="x-small"
                    color="primary"
                    v-bind="props"
                    :disabled="disabledButtonSelectAll"
                    @click="toggleMultiSelect"
                  >
                    <v-icon size="large">
                      {{ multiSelectMode ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank-outline' }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("strings.selectAllPrompt") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-form>
      </div>

      <div style="overflow-y: auto; overflow-x: hidden; flex: 1 1 auto;">
        <v-divider class="mt-3 mb-3" />
        <v-row class="px-4">
          <v-col cols="12">
            <v-treeview
              v-model:selected="selectedPaths"
              @update:selected="onTreeSelection"
              :items="treeItems"
              :select-strategy="multiSelectMode ? 'leaf' : 'single-leaf'"
              item-value="jsonPath"
              selectable
              open-on-click
              indent-lines="simple"
            />
          </v-col>
        </v-row>
      </div>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn color="primary darken-1" variant="text" @click="close">
          {{ $t("labels.cancel") }}
        </v-btn>
        <v-btn
          color="secondary darken-1"
          variant="text"
          :disabled="!valid"
          @click="confirm"
        >
          {{ $t("labels.insert") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar
      v-model="showAutoSelectSnackbar"
      color="info"
    >
      {{ $t("strings.selectAllPrompt") }}.
      <template #actions>
        <v-btn color="white" variant="text" @click="toggleMultiSelect">
          {{ $t("labels.selectAll") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import jsonpath from 'jsonpath';

const props = defineProps({
  modelValue: Boolean,
  jsonData: Object,
  initJsonPath: String
});

const emit = defineEmits(['update:modelValue', 'confirm']);

const open = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const jsonPath = ref('');
const selectedPaths = ref([]);
const valid = ref(false);
const multiSelectMode = ref(false);
const disabledButtonSelectAll = ref(true);

const showAutoSelectSnackbar = ref(false);

watch(() => props.modelValue, async (newVal) => {
  if (newVal && props.initJsonPath) {
    jsonPath.value = props.initJsonPath;
    
    if (props.initJsonPath.includes('[*]')) {
      toggleMultiSelect();
    }
  }
});

watch(jsonPath, (newVal) => {
  selectedPaths.value = [];

  try {
    const paths = jsonpath.paths(props.jsonData, newVal);
    const stringified = paths.map(p => jsonpath.stringify(p));
    selectedPaths.value = stringified;

    if (checkIfFieldExistsInAllArrayItems(newVal)) {
      managementEnableSelectAllButton(true)
    } else {
      managementDisableSelectAll();
    }
  } catch {
    selectedPaths.value = [];
    managementDisableSelectAll();
  }
});

function close() {
  jsonPath.value = '';
  selectedPaths.value = [];
  managementDisableSelectAll();
  emit('update:modelValue', false);
}

function confirm() {
  emit('confirm', jsonPath.value);
  close();
}

async function onTreeSelection(newVal) {
  if (newVal?.length == 1) {
    jsonPath.value = newVal[0];
  } else if (newVal?.length > 1 && !arePathsStructurallyEqual(newVal)) {
    jsonPath.value = newVal[newVal.length - 1];
    selectedPaths.value = [jsonPath.value]
  } else {
    jsonPath.value = "";
    selectedPaths.value = [];
  }

  if (checkIfFieldExistsInAllArrayItems(jsonPath.value)) {
    managementEnableSelectAllButton(true);
  } else {
    managementDisableSelectAll();
  }
}

function normalizePath(path) {
  return path.replace(/\[\d+\]/g, '[*]');
}

function arePathsStructurallyEqual(paths) {
  if (paths.length === 0) return true;

  const normalized = paths.map(normalizePath);
  const first = normalized[0];

  return normalized.every(p => p === first);
}

function checkIfFieldExistsInAllArrayItems(path) {
  try {
    const lastArrayIndexMatch = [...path.matchAll(/\[(\d+|\*)\]/g)].pop();
    if (!lastArrayIndexMatch) return false;

    const arrayIndexPos = lastArrayIndexMatch.index;
    const arrayPath = path.slice(0, arrayIndexPos);
    const fullArrayPath = arrayPath;

    const array = jsonpath.query(props.jsonData, fullArrayPath)[0];
    if (!Array.isArray(array)) return false;

    const remainder = path.slice(arrayIndexPos + lastArrayIndexMatch[0].length);
    const keyMatch = remainder.match(/^\.(\w+)$/) || remainder.match(/^\["(.+?)"\]$/) || remainder.match(/^\['(.+?)'\]$/);
    if (!keyMatch) return false;

    const fieldKey = keyMatch[1];

    return array.every(item => fieldKey in item);
  } catch {
    return false;
  }
}

function toggleMultiSelect() {
  if (multiSelectMode.value) {
    if (selectedPaths.value.length > 0) {
      jsonPath.value = selectedPaths.value[0];
      selectedPaths.value = [jsonPath.value];
    }
    managementEnableSelectAllButton(false);
    return;
  }

  try {
    const path = jsonPath.value;

    const lastArrayAccessMatch = [...path.matchAll(/\[(\d+|\*)\]/g)].pop();
    if (!lastArrayAccessMatch) return;

    const arrayIndexPos = lastArrayAccessMatch.index;
    const arrayAccess = lastArrayAccessMatch[0];
    const arrayPath = path.slice(0, arrayIndexPos);
    const fullArrayPath = arrayPath;

    const array = jsonpath.query(props.jsonData, fullArrayPath)[0];
    if (!Array.isArray(array)) return;

    const remainder = path.slice(arrayIndexPos + arrayAccess.length);
    const dotKeyMatch = remainder.match(/^\.(\w+)$/);
    const quotedKeyMatch = remainder.match(/^\["(.+?)"\]$/) || remainder.match(/^\['(.+?)'\]$/);

    let fieldKey;
    let formattedKey;

    if (dotKeyMatch) {
      fieldKey = dotKeyMatch[1];
      formattedKey = `.${fieldKey}`;
    } else if (quotedKeyMatch) {
      fieldKey = quotedKeyMatch[1];
      formattedKey = `["${fieldKey}"]`;
    } else {
      return;
    }

    const allPaths = array.map((_, i) => `${fullArrayPath}[${i}]${formattedKey}`);
    selectedPaths.value = allPaths;

    jsonPath.value = `${fullArrayPath}[*]${formattedKey}`;

    managementEnableSelectAllButton(false);
  } catch {
    return;
  }
}

function managementDisableSelectAll() {
  multiSelectMode.value = false;
  disabledButtonSelectAll.value = true;
  showAutoSelectSnackbar.value = false;
}

async function managementEnableSelectAllButton(visualizeSnackbar) {
  multiSelectMode.value = false;
  disabledButtonSelectAll.value = false;

  managementDisableOrEnableMultiSelectMode();

  if (visualizeSnackbar) {
    showAutoSelectSnackbar.value = true;

    await new Promise((resolve) => setTimeout(resolve, 4000));

    showAutoSelectSnackbar.value = false;
  }
}

function managementDisableOrEnableMultiSelectMode() {
  if (jsonPath.value.includes('[*]')) {
    multiSelectMode.value = true;
  } else {
    multiSelectMode.value = false;
  }
}

function transformRecursive(data, keyName = '', currentPath = '$') {
  const escapeKey = (key) =>
    /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key) ? `.${key}` : `["${key}"]`;

  if (Array.isArray(data)) {
    const children = data.map((item, i) =>
      transformRecursive(item, '', `${currentPath}[${i}]`)
    ).flat();

    return keyName
      ? { title: `${keyName}[]`, children }
      : children;
  } else if (typeof data === 'object' && data !== null) {
    const children = Object.entries(data).map(([key, val]) =>
      transformRecursive(val, key, `${currentPath}${escapeKey(key)}`)
    ).flat();

    return keyName
      ? { title: `${keyName}`, children }
      : children;
  } else {
    return {
      title: `${keyName}: ${data}`,
      jsonPath: currentPath,
    };
  }
}

const treeItems = computed(() => transformRecursive(props.jsonData));
</script>
